import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getLanding } from "../lib/api";
import DOMPurify from "dompurify";
import ProductCard from "../components/ProductCard";
import PromoBar from "../components/PromoBar";
import Seo from "../components/Seo";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function Landing() {
  const { slug } = useParams();
  const [l, setL] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setL(null);
    setNotFound(false);
    getLanding(slug).then(setL).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="font-heading text-2xl">Página no encontrada</h1></div>;
  if (!l) return <div className="max-w-5xl mx-auto px-4 py-20"><div className="h-64 bg-secondary animate-pulse rounded-sm" /></div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `FABRIPAN — ${l.town}`,
    areaServed: l.town,
    telephone: "+34 931739860",
    address: { "@type": "PostalAddress", addressLocality: "Santa Perpetua de la Moguda", addressCountry: "ES" },
  };

  return (
    <div>
      <Seo title={l.title} description={l.meta_description || `Equipamiento profesional de panadería en ${l.town}: bandejas, armarios de transporte, carros y moldes. FABRIPAN, directo de fábrica a su panadería.`} keywords={l.keywords || `equipamiento panadería ${l.town}, bandejas panadería ${l.town}, maquinaria panadería ${l.town}, suministros panadería ${l.town}`} jsonLd={jsonLd} />
      {l.promo && l.promo.enabled && <PromoBar promo={l.promo} testid="landing-promo-bar" />}
      <section className="bg-brand-ink text-white">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <span className="inline-flex items-center gap-1.5 font-mono text-xs text-brand border border-brand/40 px-3 py-1 rounded-sm mb-5"><MapPin className="h-3 w-3" /> {l.town}</span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter">Equipamiento de panadería en {l.town}</h1>
          <p className="mt-4 text-white/70 max-w-2xl">{l.intro}</p>
          <Link to="/tienda" className="inline-flex items-center gap-2 mt-6 bg-brand hover:bg-brand-dark text-white font-semibold px-6 py-3 rounded-sm transition-colors" data-testid="landing-cta">Ver catálogo <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="prose prose-neutral max-w-none text-muted-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(l.content) }} />

        {l.products && l.products.length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-2xl tracking-tighter mb-5">Productos destacados</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {l.products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        {l.faq && l.faq.length > 0 && (
          <div className="mt-12 max-w-2xl">
            <h2 className="font-heading font-bold text-2xl tracking-tighter mb-4">Preguntas frecuentes</h2>
            <Accordion type="single" collapsible>
              {l.faq.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        <div className="mt-12 border border-brand/40 bg-brand/5 rounded-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-heading font-semibold text-lg">¿Necesita un presupuesto para {l.town}?</p>
            <p className="text-sm text-muted-foreground">Enviamos su pedido personalizado a su empresa.</p>
          </div>
          <a href="tel:+34931739860" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-sm hover:bg-brand-dark transition-colors"><Phone className="h-4 w-4" /> +34 931 739 860</a>
        </div>
      </div>
    </div>
  );
}
